import React from "react";
import marked from "marked";
import { DrivingDistance } from "@wayke-se/ecom";
import StoreAction from "../constants/store-action";
import UserEvent from "../constants/user-event";
import { validateInsurance } from "../tools/data-validation";
import Alert from "../components/alert";
import Spinner from "../components/spinner";
import { getDrivingDistanceLabel } from "../utils/insurance";
import { addSizeQuery } from "../utils/image";
import { validatePersonalNumber } from "../utils/validation";
import { htmlEncode } from "../utils/encode";
const markedRenderer = new marked.Renderer();
const linkRenderer = markedRenderer.link;
markedRenderer.link = (href, title, text) => {
    const html = linkRenderer.call(markedRenderer, href, title, text);
    return html.replace(/<a/, '<a target="_blank"');
};
const drivingDistanceOptions = Object.keys(DrivingDistance);
const getIndexFromOption = (option) => {
    return drivingDistanceOptions.indexOf(option);
};
class InsuranceInformationDefinition extends React.Component {
    constructor(props) {
        super(props);
        this.handleExpectedDrivingDistanceChange = this.handleExpectedDrivingDistanceChange.bind(this);
        this.handleProceedClick = this.handleProceedClick.bind(this);
        this.handleProceedAfterUpdate = this.handleProceedAfterUpdate.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.state = {
            expectedDrivingDistanceIndex: this.props.data.insurance
                .expectedDrivingDistance
                ? getIndexFromOption(this.props.data.insurance.expectedDrivingDistance)
                : 0,
            hasRequestError: false,
        };
    }
    componentDidMount() {
        const insurancePersonalNumber = this.props.data.insurance
            .personalNumber;
        const customerPersonalNumber = this.props.data.customer.personalNumber;
        if (!insurancePersonalNumber && !!customerPersonalNumber) {
            this.props.dispatchStoreAction(StoreAction.UPDATE_NAMED_VALUE, {
                type: "insurance",
                name: "personalNumber",
                value: customerPersonalNumber,
            });
        }
    }
    handleExpectedDrivingDistanceChange(e) {
        this.setState({
            expectedDrivingDistanceIndex: e.target.selectedIndex,
        }, () => {
            this.props.dispatchStoreAction(StoreAction.UPDATE_NAMED_VALUE, {
                type: "insurance",
                name: "expectedDrivingDistance",
                value: drivingDistanceOptions[this.state.expectedDrivingDistanceIndex],
            });
        });
    }
    handleWantsToSeeInsuranceOptionsChange(wantsToSeeInsuranceOptions) {
        this.props.dispatchStoreAction(StoreAction.UPDATE_NAMED_VALUE, {
            type: "insurance",
            name: "wantsToSeeInsuranceOptions",
            value: wantsToSeeInsuranceOptions,
        }, () => {
            if (wantsToSeeInsuranceOptions) {
                this.handleProceedClick();
            }
            else {
                this.props.onIncompleteUserEvent(UserEvent.INSURANCE_SKIPPED);
                this.props.onProceedToNextStep();
            }
        });
    }
    handleProceedClick() {
        this.props.dispatchStoreAction(StoreAction.UPDATE_NAMED_VALUE, {
            type: "insurance",
            name: "expectedDrivingDistance",
            value: drivingDistanceOptions[this.state.expectedDrivingDistanceIndex],
        }, (state) => {
            this.handleProceedAfterUpdate(state);
        });
    }
    handleProceedAfterUpdate(state) {
        const isValidInsurance = validateInsurance(state.insurance);
        if (!isValidInsurance) {
            return this.props.dispatchStoreAction(StoreAction.INTERACT_SET_ALL_FOR_TYPE, "insurance");
        }
        this.setState({
            hasRequestError: false,
        }, () => {
            this.props.onFetchInsuranceOptions((isSuccessful) => {
                if (isSuccessful) {
                    this.props.onProceedToNextStep();
                }
                else {
                    this.setState({ hasRequestError: true });
                }
            });
        });
    }
    handleInputChange(e) {
        this.props.dispatchStoreAction(StoreAction.UPDATE_NAMED_VALUE, {
            type: "insurance",
            name: e.target.name,
            value: e.target.value,
        });
    }
    handleBlur(e) {
        this.props.dispatchStoreAction(StoreAction.INTERACT_UPDATE_SPECIFIC, {
            type: "insurance",
            name: e.target.name,
        });
    }
    render() {
        const optionValues = drivingDistanceOptions.map((o) => getDrivingDistanceLabel(o));
        const optionItems = optionValues.map((v, index) => (React.createElement("option", { key: index }, v)));
        const selectedValue = optionValues[this.state.expectedDrivingDistanceIndex];
        const hasPersonalNumberError = this.props.data.interact.insurance.personalNumber &&
            !validatePersonalNumber(this.props.data.insurance.personalNumber);
        const insuranceOption = this.props.orderOptions.getInsuranceOption();
        const scaledImage = addSizeQuery(insuranceOption.logo, 100, 60);
        let markdownEcomInsuranceText;
        if (insuranceOption.ecomInsuranceText) {
            const encodedEcomInsuranceText = htmlEncode(insuranceOption.ecomInsuranceText);
            markdownEcomInsuranceText = marked(encodedEcomInsuranceText, {
                renderer: markedRenderer,
            });
        }
        const onShowInsuranceAlternatives = () => this.handleWantsToSeeInsuranceOptionsChange(true);
        const onSkipInsuranceAlternatives = () => this.handleWantsToSeeInsuranceOptionsChange(false);
        return (React.createElement("div", { className: "page-main" },
            React.createElement("section", { className: "page-section" },
                React.createElement("div", { "data-ecom-columnrow": "" },
                    React.createElement("div", { className: "column" },
                        React.createElement("h1", { className: "h6 no-margin" }, "F\u00F6rs\u00E4kring")),
                    scaledImage && (React.createElement("div", { className: "column minimal" },
                        React.createElement("img", { src: scaledImage, alt: "Logotype", className: "l-block", style: {
                                maxWidth: "100px",
                                maxHeight: "60px",
                            } }))))),
            React.createElement("section", { className: "page-section" },
                React.createElement("div", { "data-ecom-content": "" },
                    React.createElement("p", null,
                        "Vill du teckna f\u00F6rs\u00E4kring via",
                        " ",
                        insuranceOption.title,
                        " p\u00E5 din nya bil?"),
                    markdownEcomInsuranceText && (React.createElement("div", { dangerouslySetInnerHTML: {
                            __html: markdownEcomInsuranceText,
                        } })),
                    React.createElement("p", null, "Skriv in ditt personnummer och din uppskattade k\u00F6rstr\u00E4cka f\u00F6r att se din f\u00F6rs\u00E4kringskostnad. Vill du teckna en f\u00F6rs\u00E4kring via Volvia p\u00E5 din nya bil? Ange din uppskattade k\u00F6rstr\u00E4cka f\u00F6r att se din f\u00F6rs\u00E4kringskostnad. D\u00E4refter presenterar vi f\u00F6rslag p\u00E5 f\u00F6rs\u00E4kringar som passar dig och din nya bil. I b\u00E5de hel- och halvf\u00F6rs\u00E4kring ing\u00E5r trafikf\u00F6rs\u00E4kring som \u00E4r obligatoriskt att ha."))),
            React.createElement("section", { className: "page-section" },
                React.createElement("div", { "data-ecom-form": "" },
                    React.createElement("div", { className: `form-group ${hasPersonalNumberError ? " has-error" : ""}` },
                        React.createElement("label", { "data-ecom-inputlabel": "", htmlFor: "insurance-input-personalnr" }, "Personnummer"),
                        React.createElement("div", { "data-ecom-inputtext": "" },
                            React.createElement("input", { type: "text", id: "insurance-input-personalnr", name: "personalNumber", placeholder: "\u00C5\u00C5\u00C5\u00C5MMDD-XXXX", value: this.props.data.insurance.personalNumber, onChange: this.handleInputChange, onBlur: this.handleBlur })),
                        React.createElement("div", { className: "form-alert" }, "Ange personnummer i formatet \u00C5\u00C5\u00C5\u00C5MMDD-XXXX")),
                    React.createElement("div", { className: "form-group" },
                        React.createElement("label", { "data-ecom-inputlabel": "", htmlFor: "insurance-input-mileage" }, "Uppskattad k\u00F6rstr\u00E4cka per \u00E5r"),
                        React.createElement("div", { "data-ecom-select": "" },
                            React.createElement("select", { className: "select", value: selectedValue, onChange: this.handleExpectedDrivingDistanceChange }, optionItems))),
                    this.state.hasRequestError && (React.createElement("div", { className: "form-group" },
                        React.createElement(Alert, { message: "Tyv\u00E4rr hittades inga f\u00F6rs\u00E4kringsalternativ f\u00F6r det angivna personnumret." }))))),
            !this.props.isWaitingForResponse && (React.createElement("section", { className: "page-section" },
                React.createElement("div", { className: "repeat-m" },
                    React.createElement("button", { "data-ecom-button": "full-width", onClick: onShowInsuranceAlternatives }, "Visa f\u00F6rs\u00E4kringar")),
                React.createElement("div", { className: "repeat-m" },
                    React.createElement("button", { "data-ecom-button": "full-width light", onClick: onSkipInsuranceAlternatives }, "Hoppa \u00F6ver detta steg")))),
            this.props.isWaitingForResponse && (React.createElement("section", { className: "page-section" },
                React.createElement("div", { className: "form-group" },
                    React.createElement(Spinner, null))))));
    }
}
export default InsuranceInformationDefinition;
